import PerformanceModule from '../components/personnel/PerformanceModule'
import DisciplinaryModule from '../components/personnel/DisciplinaryModule'
import { useState, useEffect } from 'react'

import { useDataStore } from '../stores/dataStore'
import { Users, Plus, Search, GraduationCap, Calendar, FileText, CheckCircle, XCircle, X, Download, Edit3, CheckSquare, BookOpen, AlertCircle, Trash2, Mail, MapPin, Filter, ChevronDown, ChevronUp, Play, Star, AlertTriangle } from 'lucide-react'
import './Personnel.css'

export default function Personnel() {
  console.log('Personnel component loading...')
  
  const [activeTab, setActiveTab] = useState('guards')
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [expandedRow, setExpandedRow] = useState(null)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [filterStatus, setFilterStatus] = useState('all')

  const { guards, personnel, leaves, trainings, fetchGuards, fetchPersonnel, fetchLeaves, fetchTrainings } = useDataStore()
  const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

  useEffect(() => { 
    console.log('Fetching data...')
    fetchGuards(); fetchPersonnel(); fetchLeaves(); fetchTrainings() 
  }, [])

  const showMessage = (type, text) => { 
    console.log('Message:', type, text)
    setMessage({ type, text }); 
    setTimeout(() => setMessage({ type: '', text: '' }), 5000) 
  }

  // DEBUG: Direct click handler with alert
  const testClick = () => {
    alert('Button clicked! Handler working.')
    console.log('Test click worked')
  }

  const openModal = (type, item = null) => {
    console.log('Opening modal:', type, item)
    setModalType(type)
    setSelectedItem(item)
    setFormData(item || getInitialFormData(type))
    setShowModal(true)
    setMessage({ type: '', text: '' })
  }

  const closeModal = () => { 
    console.log('Closing modal')
    setShowModal(false)
    setModalType('')
    setSelectedItem(null)
    setFormData({})
    setLoading(false)
  }

  const getInitialFormData = (type) => {
    switch(type) {
      case 'guard': return { firstName: '', lastName: '', guardId: '', phone: '', email: '', address: '', nationalId: '', dateOfBirth: '', position: '', siteAssigned: '', shiftPreference: 'day', status: 'active', hireDate: '', emergencyContact: { name: '', phone: '', relationship: '' } }
      case 'recruitment': return { firstName: '', lastName: '', email: '', phone: '', nationalId: '', positionApplied: '', expectedSalary: '', status: 'new', source: 'website', residenceAddress: '', recruitedBy: '' }
      case 'training': return { title: '', trainer: '', trainingType: '', location: '', start_date: '', end_date: '', description: '', status: 'upcoming' }
      case 'leave': return { guard_name: '', guard_id: '', type: 'annual', start_date: '', end_date: '', reason: '', status: 'pending', contact_during_leave: '' }
      default: return {}
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log('Submitting form:', modalType, formData)
    setLoading(true)
    try {
      let endpoint = ''
      let method = selectedItem ? 'PUT' : 'POST'
      switch(modalType) {
        case 'guard': endpoint = selectedItem ? `/guards/${selectedItem._id || selectedItem.id}` : '/guards'; break
        case 'recruitment': endpoint = selectedItem ? `/personnel/${selectedItem._id || selectedItem.id}` : '/personnel'; break
        case 'training': endpoint = selectedItem ? `/training/${selectedItem._id || selectedItem.id}` : '/training'; break
        case 'leave': endpoint = selectedItem ? `/leave/${selectedItem._id || selectedItem.id}` : '/leave'; break
        default: return
      }
      
      console.log('API call:', method, `${API_URL}${endpoint}`)
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify(formData)
      })
      
      console.log('Response:', response.status)
      if (response.ok) {
        showMessage('success', `${modalType} ${selectedItem ? 'updated' : 'created'} successfully!`)
        fetchGuards(); fetchPersonnel(); fetchLeaves(); fetchTrainings()
        setTimeout(() => closeModal(), 1500)
      } else {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }))
        showMessage('error', error.message || 'Operation failed')
      }
    } catch (err) { 
      console.error('Error:', err)
      showMessage('error', 'Network error. Please try again.')
    } finally { 
      setLoading(false) 
    }
  }

  const handleDelete = async (item, type) => {
    console.log('Delete clicked:', type, item)
    if (!window.confirm(`Are you sure you want to delete this ${type}? This action cannot be undone.`)) return
    
    setLoading(true)
    try {
      let endpoint = ''
      switch(type) {
        case 'guard': endpoint = `/guards/${item._id || item.id}`; break
        case 'recruitment': endpoint = `/personnel/${item._id || item.id}`; break
        case 'training': endpoint = `/training/${item._id || item.id}`; break
        case 'leave': endpoint = `/leave/${item._id || item.id}`; break
        default: return
      }
      
      console.log('Delete API call:', `${API_URL}${endpoint}`)
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      
      console.log('Delete response:', response.status)
      if (response.ok) {
        showMessage('success', `${type} deleted successfully!`)
        fetchGuards(); fetchPersonnel(); fetchLeaves(); fetchTrainings()
      } else {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }))
        showMessage('error', error.message || 'Delete failed')
      }
    } catch (err) { 
      console.error('Delete error:', err)
      showMessage('error', 'Network error. Please try again.')
    } finally { 
      setLoading(false) 
    }
  }

  const handleStatusUpdate = async (item, type, newStatus) => {
    console.log('Status update:', type, newStatus)
    setLoading(true)
    try {
      let endpoint = ''
      switch(type) {
        case 'personnel': endpoint = `/personnel/${item._id || item.id}`; break
        case 'leave': endpoint = `/leave/${item._id || item.id}`; break
        case 'training': endpoint = `/training/${item._id || item.id}`; break
        default: return
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ ...item, status: newStatus })
      })
      
      if (response.ok) {
        showMessage('success', `Status updated to ${newStatus}!`)
        fetchGuards(); fetchPersonnel(); fetchLeaves(); fetchTrainings()
      } else {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }))
        showMessage('error', error.message || 'Update failed')
      }
    } catch (err) { 
      showMessage('error', 'Network error. Please try again.')
    } finally { 
      setLoading(false) 
    }
  }

  const handleExport = (type) => {
    let data = []
    let filename = ''
    let headers = []
    switch(type) {
      case 'guards': data = filteredGuards; filename = 'guards-list.csv'; headers = ['Badge #', 'Name', 'Phone', 'Email', 'Location', 'Shift', 'Status', 'Hire Date']; break
      case 'recruitment': data = filteredRecruitment; filename = 'recruitment-list.csv'; headers = ['Applicant ID', 'Name', 'Phone', 'Email', 'Position', 'Status', 'Source']; break
      case 'training': data = filteredTraining; filename = 'training-list.csv'; headers = ['Title', 'Trainer', 'Location', 'Start Date', 'End Date', 'Status']; break
      case 'leave': data = filteredLeaves; filename = 'leave-list.csv'; headers = ['Guard Name', 'Type', 'Start Date', 'End Date', 'Days', 'Status', 'Reason']; break
      default: return
    }
    
    if (data.length === 0) { showMessage('error', 'No data to export'); return }
    
    const csvRows = data.map(row => {
      switch(type) {
        case 'guards': return [row.guardId, row.name, row.phone, row.email, row.siteAssigned, row.shift, row.status, row.hireDate].map(v => `"${v || ''}"`).join(',')
        case 'recruitment': return [row.applicantId, `${row.firstName} ${row.lastName}`, row.phone, row.email, row.positionApplied, row.status, row.source].map(v => `"${v || ''}"`).join(',')
        case 'training': return [row.title, row.trainer, row.siteAssigned, row.start_date, row.end_date, row.status].map(v => `"${v || ''}"`).join(',')
        case 'leave': 
          const days = row.start_date && row.end_date ? Math.ceil((new Date(row.end_date) - new Date(row.start_date)) / (1000 * 60 * 60 * 24)) + 1 : 0
          return [row.guard_name, row.type, row.start_date, row.end_date, days, row.status, row.reason].map(v => `"${v || ''}"`).join(',')
        default: return ''
      }
    })
    
    const csv = [headers.join(','), ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
    showMessage('success', `${filename} exported successfully!`)
  }

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const sortData = (data, key, direction) => {
    if (!key) return data
    return [...data].sort((a, b) => {
      let aVal = a[key] || ''
      let bVal = b[key] || ''
      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()
      if (aVal < bVal) return direction === 'asc' ? -1 : 1
      if (aVal > bVal) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  const filteredGuards = guards?.filter(g => {
    const matchesSearch = (g?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) || g?.lastName?.toLowerCase().includes(searchTerm.toLowerCase())) || 
                         g?.guardId?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         g?.siteAssigned?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         g?.phone?.includes(searchTerm)
    const matchesStatus = filterStatus === 'all' || g?.status === filterStatus
    return matchesSearch && matchesStatus
  }) || []

  const filteredRecruitment = personnel?.filter(p => {
    const matchesSearch = p?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         p?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p?.positionApplied?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         p?.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p?.applicantId?.includes(searchTerm)
    const matchesStatus = filterStatus === 'all' || p?.status === filterStatus
    return matchesSearch && matchesStatus
  }) || []

  const filteredTraining = trainings?.filter(t => {
    const matchesSearch = t?.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         t?.trainer?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         t?.status?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || t?.status === filterStatus
    return matchesSearch && matchesStatus
  }) || []

  const filteredLeaves = leaves?.filter(l => {
    const matchesSearch = l?.guard_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         l?.type?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         l?.status?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || l?.status === filterStatus
    return matchesSearch && matchesStatus
  }) || []

  const sortedGuards = sortData(filteredGuards, sortConfig.key, sortConfig.direction)
  const sortedRecruitment = sortData(filteredRecruitment, sortConfig.key, sortConfig.direction)
  const sortedTraining = sortData(filteredTraining, sortConfig.key, sortConfig.direction)
  const sortedLeaves = sortData(filteredLeaves, sortConfig.key, sortConfig.direction)

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ChevronDown size={14} className="sort-icon" />
    return sortConfig.direction === "asc" ? <ChevronUp size={14} className="sort-icon active" /> : <ChevronDown size={14} className="sort-icon active" />
  }


  const getStatusBadgeClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'active':
      case 'hired':
      case 'approved':
      case 'completed':
        return 'badge-success'
      case 'pending':
      case 'new':
      case 'upcoming':
        return 'badge-warning'
      case 'on_leave':
      case 'interview':
      case 'ongoing':
        return 'badge-info'
      case 'suspended':
      case 'rejected':
      case 'cancelled':
        return 'badge-danger'
      default:
        return 'badge-secondary'
    }
  }

  console.log('Rendering Personnel, showModal:', showModal)

  return (
    <div className="personnel">
      <div className="page-header">
        <div>
          <h1><Users size={28} /> Personnel Management</h1>
          <p>Manage guards, recruitment, training programs, and leave requests</p>
        </div>
        <div>
          <button onClick={testClick} style={{marginRight: '10px', padding: '5px 10px', background: 'red', color: 'white'}}>
            TEST CLICK
          </button>
                    {(activeTab === 'guards' || activeTab === 'recruitment' || activeTab === 'training' || activeTab === 'leave') && (
            <button className="btn btn-primary" onClick={() => openModal(activeTab === 'guards' ? 'guard' : activeTab === 'recruitment' ? 'recruitment' : activeTab === 'training' ? 'training' : 'leave')}>
              <Plus size={18} /> {activeTab === 'guards' ? 'Add Guard' : activeTab === 'recruitment' ? 'Add Applicant' : activeTab === 'training' ? 'Add Training' : 'Request Leave'}
            </button>
          )}
        </div>
      </div>

      {message.text && !showModal && (
        <div className={`alert alert-${message.type} mb-3`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${activeTab === 'guards' ? 'active' : ''}`} onClick={() => { setActiveTab('guards'); setFilterStatus('all'); setSearchTerm('') }}>
          <Users size={18} /> Guards ({filteredGuards.length})
        </button>
        <button className={`tab ${activeTab === 'recruitment' ? 'active' : ''}`} onClick={() => { setActiveTab('recruitment'); setFilterStatus('all'); setSearchTerm('') }}>
          <FileText size={18} /> Recruitment ({filteredRecruitment.length})
        </button>
        <button className={`tab ${activeTab === 'training' ? 'active' : ''}`} onClick={() => { setActiveTab('training'); setFilterStatus('all'); setSearchTerm('') }}>
          <GraduationCap size={18} /> Training ({filteredTraining.length})
        </button>
        <button className={`tab ${activeTab === 'leave' ? 'active' : ''}`} onClick={() => { setActiveTab('leave'); setFilterStatus('all'); setSearchTerm('') }}>
          <Calendar size={18} /> Leave ({filteredLeaves.length})
        </button>
        <button 
          className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          <Star size={18} />
          Performance
        </button>
        <button 
          className={`tab ${activeTab === 'disciplinary' ? 'active' : ''}`}
          onClick={() => setActiveTab('disciplinary')}
        >
          <AlertTriangle size={18} />
          Disciplinary
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={18} />
          <input 
            type="text" 
            placeholder={`Search ${activeTab}...`} 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            {activeTab === 'guards' && (
              <>
                <option value="active">Active</option>
                <option value="on_leave">On Leave</option>
                <option value="suspended">Suspended</option>
                <option value="resigned">Resigned</option>
                <option value="deserter">Deserter</option>
                <option value="terminated">Terminated</option>
              </>
            )}
            {activeTab === 'recruitment' && (
              <>
                <option value="new">New</option>
                <option value="screening">Screening</option>
                <option value="interview">Interview</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </>
            )}
            {activeTab === 'training' && (
              <>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
              </>
            )}
            {activeTab === 'leave' && (
              <>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </>
            )}
          </select>
        </div>
        <button className="btn btn-outline" onClick={() => handleExport(activeTab)}>
          <Download size={16} /> Export
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'guards' && (
          <div className="section">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('guardId')}>Badge # {renderSortIcon('guardId')}</th>
                    <th onClick={() => handleSort('firstName')}>Name {renderSortIcon('firstName')}</th>
                    <th>Contact</th>
                    <th onClick={() => handleSort('siteAssigned')}>Location {renderSortIcon('siteAssigned')}</th>
                    <th>Shift</th>
                    <th onClick={() => handleSort('status')}>Status {renderSortIcon('status')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGuards.length > 0 ? sortedGuards.map(guard => (
                    <tr key={guard._id || guard.id} className={expandedRow === guard._id ? 'expanded' : ''}>
                      <td><span className="font-mono font-bold">{guard.guardId}</span></td>
                      <td>
                        <div className="cell-primary">{guard.firstName} {guard.lastName}</div>
                        {expandedRow === guard._id && (
                          <div className="cell-details">
                            <div><Mail size={12} /> {guard.email || 'N/A'}</div>
                            <div><MapPin size={12} /> {guard.address || 'N/A'}</div>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="cell-primary">{guard.phone}</div>
                        {guard.emergency_contact && <div className="cell-secondary">Emerg: {guard.emergency_contact}</div>}
                      </td>
                      <td>{guard.siteAssigned}</td>
                      <td><span className="badge badge-info">{guard.shiftPreference}</span></td>
                      <td><span className={`badge ${getStatusBadgeClass(guard.status)}`}>{guard.status}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => openModal('guard', guard)} title="Edit">
                            <Edit3 size={14} />
                          </button>
                          <button className="btn btn-sm btn-outline" onClick={() => setExpandedRow(expandedRow === guard._id ? null : guard._id)} title="View Details">
                            {expandedRow === guard._id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(guard, 'guard')} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">No guards found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'recruitment' && (
          <div className="section">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Applicant ID</th>
                    <th onClick={() => handleSort('firstName')}>Name {renderSortIcon('firstName')}</th>
                    <th>Contact</th>
                    <th onClick={() => handleSort('positionApplied')}>Position {renderSortIcon('positionApplied')}</th>
                    <th onClick={() => handleSort('status')}>Status {renderSortIcon('status')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRecruitment.length > 0 ? sortedRecruitment.map(person => (
                    <tr key={person._id || person.id}>
                      <td><span className="font-mono">{person.applicantId}</span></td>
                      <td>
                        <div className="cell-primary">{person.firstName} {person.lastName}</div>
                        <div className="cell-secondary">{person.nationalId}</div>
                      </td>
                      <td>
                        <div className="cell-primary">{person.phone}</div>
                        <div className="cell-secondary">{person.email}</div>
                      </td>
                      <td>{person.positionApplied}</td>
                      <td><span className={`badge ${getStatusBadgeClass(person.status)}`}>{person.status}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => openModal('recruitment', person)} title="Edit">
                            <Edit3 size={14} />
                          </button>
                          {person.status === 'new' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleStatusUpdate(person, 'personnel', 'screening')} title="Move to Screening">
                              <CheckSquare size={14} />
                            </button>
                          )}
                          {person.status === 'screening' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleStatusUpdate(person, 'personnel', 'interview')} title="Schedule Interview">
                              <Calendar size={14} />
                            </button>
                          )}
                          {person.status === 'interview' && (
                            <button className="btn btn-sm btn-success" onClick={() => handleStatusUpdate(person, 'personnel', 'hired')} title="Hire">
                              <CheckCircle size={14} />
                            </button>
                          )}
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(person, 'recruitment')} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">No applicants found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'training' && (
          <div className="section">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('title')}>Title {renderSortIcon('title')}</th>
                    <th>Trainer</th>
                    <th>Duration</th>
                    <th onClick={() => handleSort('status')}>Status {renderSortIcon('status')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTraining.length > 0 ? sortedTraining.map(training => (
                    <tr key={training._id || training.id}>
                      <td>
                        <div className="cell-primary">{training.title}</div>
                        <div className="cell-secondary">{training.location}</div>
                      </td>
                      <td>{training.trainer}</td>
                      <td>
                        {training.start_date && training.end_date ? (
                          <>
                            <div className="cell-primary">
                              {new Date(training.start_date).toLocaleDateString()} - {new Date(training.end_date).toLocaleDateString()}
                            </div>
                            <div className="cell-secondary">
                              {Math.ceil((new Date(training.end_date) - new Date(training.start_date)) / (1000 * 60 * 60 * 24)) + 1} days
                            </div>
                          </>
                        ) : 'Not scheduled'}
                      </td>
                      <td><span className={`badge ${getStatusBadgeClass(training.status)}`}>{training.status}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => openModal('training', training)} title="Edit">
                            <Edit3 size={14} />
                          </button>
                          {training.status === 'upcoming' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleStatusUpdate(training, 'training', 'ongoing')} title="Start Training">
                              <Play size={14} />
                            </button>
                          )}
                          {training.status === 'ongoing' && (
                            <button className="btn btn-sm btn-success" onClick={() => handleStatusUpdate(training, 'training', 'completed')} title="Complete">
                              <CheckCircle size={14} />
                            </button>
                          )}
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(training, 'training')} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">No training programs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'leave' && (
          <div className="section">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('guard_name')}>Guard {renderSortIcon('guard_name')}</th>
                    <th>Type</th>
                    <th>Duration</th>
                    <th onClick={() => handleSort('status')}>Status {renderSortIcon('status')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLeaves.length > 0 ? sortedLeaves.map(leave => {
                    const days = leave.start_date && leave.end_date 
                      ? Math.ceil((new Date(leave.end_date) - new Date(leave.start_date)) / (1000 * 60 * 60 * 24)) + 1 
                      : 0
                    return (
                      <tr key={leave._id || leave.id}>
                        <td>
                          <div className="cell-primary">{leave.guard_name}</div>
                          {leave.contact_during_leave && (
                            <div className="cell-secondary">Contact: {leave.contact_during_leave}</div>
                          )}
                        </td>
                        <td><span className="badge badge-secondary">{leave.type}</span></td>
                        <td>
                          <div className="cell-primary">
                            {leave.start_date ? new Date(leave.start_date).toLocaleDateString() : ''} - {leave.end_date ? new Date(leave.end_date).toLocaleDateString() : ''}
                          </div>
                          <div className="cell-secondary">{days} day{days !== 1 ? 's' : ''}</div>
                        </td>
                        <td><span className={`badge ${getStatusBadgeClass(leave.status)}`}>{leave.status}</span></td>
                        <td>
                          <div className="action-buttons">
                            {leave.status === 'pending' && (
                              <>
                                <button className="btn btn-sm btn-success" onClick={() => handleStatusUpdate(leave, 'leave', 'approved')} title="Approve">
                                  <CheckCircle size={14} />
                                </button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleStatusUpdate(leave, 'leave', 'rejected')} title="Reject">
                                  <XCircle size={14} />
                                </button>
                              </>
                            )}

                            <button className="btn btn-sm btn-outline" onClick={() => openModal('leave', leave)} title="Edit">
                              <Edit3 size={14} />
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(leave, 'leave')} title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  }) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">No leave requests found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'performance' && <PerformanceModule />}
        {activeTab === 'disciplinary' && <DisciplinaryModule />}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h3>
                {modalType === 'guard' && (selectedItem ? 'Edit Guard' : 'Add New Guard')}
                {modalType === 'recruitment' && (selectedItem ? 'Edit Applicant' : 'Add Applicant')}
                {modalType === 'training' && (selectedItem ? 'Edit Training' : 'Add Training Program')}
                {modalType === 'leave' && (selectedItem ? 'Edit Leave Request' : 'Request Leave')}
              </h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            {message.text && (
              <div className={`alert alert-${message.type}`}>
                {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {modalType === 'guard' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input type="text" name="firstName" value={formData.firstName || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input type="text" name="lastName" value={formData.lastName || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Guard ID *</label>
                    <input type="text" name="guardId" value={formData.guardId || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input type="tel" name="phone" value={formData.phone || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" name="email" value={formData.email || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Emergency Contact</label>
                    <input type="tel" name="emergencyContact" value={formData.emergencyContact || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Site *</label>
                    <input type="text" name="siteAssigned" value={formData.siteAssigned || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Shift</label>
                    <select name="shiftPreference" value={formData.shiftPreference || 'day'} onChange={handleInputChange}>
                      <option value="day">Day</option>
                      <option value="night">Night</option>
                      <option value="rotation">Rotation</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status || 'active'} onChange={handleInputChange}>
                      <option value="active">Active</option>
                      <option value="on_leave">On Leave</option>
                      <option value="suspended">Suspended</option>
                      <option value="resigned">Resigned</option>
                      <option value="deserter">Deserter</option>
                      <option value="terminated">Terminated</option>
                    </select>
                  </div>
                  {(formData.status === 'terminated' || formData.status === 'resigned' || formData.status === 'deserter') && (
  <div className="form-group form-col-2">
    <label>Reason / Cause *</label>
    <textarea 
      name="terminationReason" 
      value={formData.terminationReason || ''} 
      onChange={handleInputChange}
      placeholder="Enter reason for termination, resignation, or desertion"
      rows="2"
      required
    />
  </div>
)}
                  <div className="form-group">
                    <label>Hire Date</label>
                    <input type="date" name="hireDate" value={formData.hireDate ? formData.hireDate.split('T')[0] : ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group form-col-2">
                    <label>Address</label>
                    <textarea name="address" value={formData.address || ''} onChange={handleInputChange} rows="2" />
                  </div>
                </div>
              )}

              {modalType === 'recruitment' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input type="text" name="firstName" value={formData.firstName || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input type="text" name="lastName" value={formData.lastName || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>National ID</label>
                    <input type="text" name="nationalId" value={formData.nationalId || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input type="tel" name="phone" value={formData.phone || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" name="email" value={formData.email || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Position Applied *</label>
                    <input type="text" name="positionApplied" value={formData.positionApplied || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Expected Salary (KES)</label>
                    <input type="number" name="expectedSalary" value={formData.expectedSalary || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Source</label>
                    <select name="source" value={formData.source || 'website'} onChange={handleInputChange}>
                      <option value="website">Website</option>
                      <option value="referral">Referral</option>
                      <option value="agency">Agency</option>
                      <option value="walk_in">Walk-in</option>
                      <option value="social_media">Social Media</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="form-group">
  <label>Residence Address</label>
  <input 
    type="text" 
    name="residenceAddress" 
    value={formData.residenceAddress || ''} 
    onChange={handleInputChange} 
    placeholder="Enter residence address"
  />
</div>
<div className="form-group">
  <label>Recruited By</label>
  <input 
    type="text" 
    name="recruitedBy" 
    value={formData.recruitedBy || ''} 
    onChange={handleInputChange} 
    placeholder="Enter recruiter name"
  />
</div>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status || 'new'} onChange={handleInputChange}>
                      <option value="new">New</option>
                      <option value="screening">Screening</option>
                      <option value="interview">Interview</option>
                      <option value="reference_check">Reference Check</option>
                      <option value="offer">Offer</option>
                      <option value="hired">Hired</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="form-group form-col-2">
                    <label>Notes</label>
                    <textarea name="notes" value={formData.notes || ''} onChange={handleInputChange} rows="3" />
                  </div>
                </div>
              )}

              {modalType === 'training' && (
                <div className="form-grid">
                  <div className="form-group form-col-2">
                    <label>Training Title *</label>
                    <input type="text" name="title" value={formData.title || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Trainer/Instructor</label>
                    <input type="text" name="trainer" value={formData.trainer || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Training Type/Level *</label>
                    <select name="trainingType" value={formData.trainingType || ''} onChange={handleInputChange} required>
                      <option value="">Select Training Type</option>
                      <option value="management">Management</option>
                      <option value="entry_level">Entry Level</option>
                      <option value="supervisory">Supervisory</option>
                      <option value="mandatory_cultural">Mandatory Cultural</option>
                      <option value="safety_security">Safety & Security</option>
                      <option value="customer_service">Customer Service</option>
                      <option value="technical_skills">Technical Skills</option>
                      <option value="first_aid_cpr">First Aid & CPR</option>
                      <option value="fire_safety">Fire Safety</option>
                      <option value="conflict_resolution">Conflict Resolution</option>
                      <option value="leadership">Leadership</option>
                      <option value="compliance_legal">Compliance & Legal</option>
                      <option value="communication">Communication</option>
                      <option value="team_building">Team Building</option>
                      <option value="onboarding">Onboarding / Orientation</option>
                      <option value="refresher">Refresher</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" name="siteAssigned" value={formData.siteAssigned || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Start Date</label>
                    <input type="date" name="start_date" value={formData.start_date ? formData.start_date.split('T')[0] : ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input type="date" name="end_date" value={formData.end_date ? formData.end_date.split('T')[0] : ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status || 'upcoming'} onChange={handleInputChange}>
                      <option value="upcoming">Upcoming</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="form-group form-col-2">
                    <label>Description</label>
                    <textarea name="description" value={formData.description || ''} onChange={handleInputChange} rows="3" />
                  </div>
                </div>
              )}

              {modalType === 'leave' && (
                <div className="form-grid">
                  <div className="form-group form-col-2">
                    <label>Guard Name *</label>
                    <input type="text" name="guard_name" value={formData.guard_name || ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Leave Type *</label>
                    <select name="type" value={formData.type || 'annual'} onChange={handleInputChange} required>
                      <option value="annual">Annual Leave</option>
                      <option value="sick">Sick Leave</option>
                      <option value="emergency">Emergency Leave</option>
                      <option value="maternity">Maternity/Paternity</option>
                      <option value="unpaid">Unpaid Leave</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status || 'pending'} onChange={handleInputChange}>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input type="date" name="start_date" value={formData.start_date ? formData.start_date.split('T')[0] : ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>End Date *</label>
                    <input type="date" name="end_date" value={formData.end_date ? formData.end_date.split('T')[0] : ''} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Contact During Leave</label>
                    <input type="tel" name="contact_during_leave" value={formData.contact_during_leave || ''} onChange={handleInputChange} />
                  </div>
                  <div className="form-group form-col-2">
                    <label>Reason</label>
                    <textarea name="reason" value={formData.reason || ''} onChange={handleInputChange} rows="3" />
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (selectedItem ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
// Build timestamp: Sun Apr 19 11:43:16 PM UTC 2026
